import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Plus, Trash2 } from 'lucide-react';

export interface InvoiceItem {
  id: number;
  description: string;
  quantity: number;
  rate: number;
}

interface InvoiceItemsTableProps {
  items: InvoiceItem[];
  onItemChange: (id: number, field: keyof InvoiceItem, value: string | number) => void;
  onAddItem: () => void;
  onRemoveItem: (id: number) => void;
}

export const InvoiceItemsTable: React.FC<InvoiceItemsTableProps> = ({
  items,
  onItemChange,
  onAddItem,
  onRemoveItem,
}) => {
  // Track which rate fields are being edited to show raw input
  const [editingRates, setEditingRates] = useState<Record<number, string>>({});

  const calculateTotal = () => {
    return items.reduce((sum, item) => sum + item.quantity * item.rate, 0);
  };

  const handleRateChange = (id: number, value: string) => {
    setEditingRates((prev) => ({ ...prev, [id]: value }));
    // Parse and update the actual value, allowing empty string to be 0
    const numValue = value === '' ? 0 : parseFloat(value);
    if (!isNaN(numValue)) {
      onItemChange(id, 'rate', numValue);
    }
  };

  const handleRateFocus = (id: number, currentValue: number) => {
    // When focusing, show empty if value is 0, otherwise show the number without leading zeros
    setEditingRates((prev) => ({
      ...prev,
      [id]: currentValue === 0 ? '' : currentValue.toString(),
    }));
  };

  const handleRateBlur = (id: number) => {
    // Clear the editing state on blur
    setEditingRates((prev) => {
      const newState = { ...prev };
      delete newState[id];
      return newState;
    });
  };

  const getRateDisplayValue = (id: number, rate: number): string => {
    // If currently editing, show the raw input value
    if (id in editingRates) {
      return editingRates[id];
    }
    // Otherwise show the formatted number (or empty if 0 for cleaner UX)
    return rate === 0 ? '0' : rate.toString();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Line Items</h3>
        <Button type="button" variant="outline" size="sm" onClick={onAddItem}>
          <Plus className="h-4 w-4 mr-2" />
          Add Item
        </Button>
      </div>

      <div className="border rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-muted">
            <tr>
              <th className="text-left p-3 font-medium">Description</th>
              <th className="text-left p-3 font-medium w-24">Quantity</th>
              <th className="text-left p-3 font-medium w-32">Rate</th>
              <th className="text-left p-3 font-medium w-32">Amount</th>
              <th className="w-12"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id} className="border-t">
                <td className="p-2">
                  <Input
                    value={item.description}
                    onChange={(e) => onItemChange(item.id, 'description', e.target.value)}
                    placeholder="Service description"
                  />
                </td>
                <td className="p-2">
                  <Input
                    type="number"
                    value={item.quantity}
                    onChange={(e) => onItemChange(item.id, 'quantity', parseFloat(e.target.value) || 0)}
                    min="0"
                    step="0.01"
                  />
                </td>
                <td className="p-2">
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                    <Input
                      type="text"
                      inputMode="decimal"
                      value={getRateDisplayValue(item.id, item.rate)}
                      onChange={(e) => handleRateChange(item.id, e.target.value)}
                      onFocus={() => handleRateFocus(item.id, item.rate)}
                      onBlur={() => handleRateBlur(item.id)}
                      className="pl-7"
                      placeholder="0.00"
                    />
                  </div>
                </td>
                <td className="p-2 text-right pr-4">
                  ${(item.quantity * item.rate).toFixed(2)}
                </td>
                <td className="p-2">
                  {items.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => onRemoveItem(item.id)}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot className="bg-muted font-semibold">
            <tr>
              <td colSpan={3} className="p-3 text-right">Total:</td>
              <td className="p-3 text-right pr-4">
                ${calculateTotal().toFixed(2)}
              </td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};
